import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { User, Save, Building, MapPin, Receipt } from 'lucide-react'
import { userAPI } from '../services/api'
import toast from 'react-hot-toast'

const Profile: React.FC = () => {
  const { t } = useTranslation()
  const [isLoading, setIsLoading] = useState(false)
  const [profile, setProfile] = useState({
    name: '',
    email: ''
  })
  const [billingInfo, setBillingInfo] = useState({
    company_name: '',
    customer_type: 'individual', // 'individual' or 'company'
    street_address: '',
    city: '',
    postal_code: '',
    country: '', // NIE automatycznie wypełniaj kraju
    vat_number: '',
    phone: ''
  })
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  })

  useEffect(() => {
    loadUserData()
  }, [])

  const loadUserData = async () => {
    try {
      // Load profile data from API first, fallback to localStorage
      try {
        const profileResponse = await userAPI.getProfile()
        setProfile({
          name: profileResponse.data.name || '',
          email: profileResponse.data.email || ''
        })
        // Update localStorage with fresh data
        localStorage.setItem('user', JSON.stringify(profileResponse.data))
      } catch (error) {
        console.error('Error loading profile from API:', error)
        // Fallback to localStorage
        const user = JSON.parse(localStorage.getItem('user') || '{}')
        setProfile({
          name: user.name || '',
          email: user.email || ''
        })
      }
      
      // Load billing info from API
      try {
        const billingResponse = await userAPI.getBillingInfo()
        setBillingInfo(billingResponse.data)
      } catch (error) {
        console.error('Error loading billing info:', error)
        // Keep default empty billing info for new users
      }
    } catch (error) {
      console.error('Error loading user data:', error)
    }
  }

  const handleProfileSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)

    try {
      const response = await userAPI.updateProfile(profile)
      localStorage.setItem('user', JSON.stringify(response.data))
      // Powiadom inne komponenty o zmianie danych użytkownika
      window.dispatchEvent(new Event('userDataUpdated'))
      toast.success(t('profile.updateSuccess'))
    } catch (error) {
      toast.error(t('profile.updateError'))
    } finally {
      setIsLoading(false)
    }
  }

  const handleBillingSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)

    try {
      await userAPI.updateBillingInfo(billingInfo)
      toast.success('Billing information updated successfully')
    } catch (error) {
      toast.error('Failed to update billing information')
    } finally {
      setIsLoading(false)
    }
  }

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error('Hasła nie są identyczne')
      return
    }

    setIsLoading(true)

    try {
      await userAPI.changePassword({
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword
      })
      toast.success(t('profile.passwordSuccess'))
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      })
    } catch (error) {
      toast.error(t('profile.passwordError'))
    } finally {
      setIsLoading(false)
    }
  }

  const countries = [
    '', 'Poland', 'Germany', 'United Kingdom', 'France', 'Italy', 'Spain', 
    'Netherlands', 'Belgium', 'Austria', 'Switzerland', 'Sweden', 'Norway', 'Denmark',
    'Finland', 'United States', 'Canada', 'Australia', 'Other'
  ]

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-white mb-2">{t('profile.title')}</h1>
        <p className="text-gray-400">Manage your account settings and billing information</p>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        {/* Personal Info */}
        <div className="bg-dark-800 rounded-lg p-6 border border-dark-700">
          <h2 className="text-xl font-semibold text-white mb-4 flex items-center">
            <User className="h-5 w-5 text-primary mr-2" />
            {t('profile.personalInfo')}
          </h2>

          <form onSubmit={handleProfileSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                {t('auth.name')}
              </label>
              <input
                type="text"
                value={profile.name}
                onChange={(e) => setProfile(prev => ({ ...prev, name: e.target.value }))}
                className="w-full px-3 py-2 bg-dark-700 border border-dark-500 rounded-md text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                {t('auth.email')}
              </label>
              <input
                type="email"
                value={profile.email}
                onChange={(e) => setProfile(prev => ({ ...prev, email: e.target.value }))}
                className="w-full px-3 py-2 bg-dark-700 border border-dark-500 rounded-md text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="flex items-center space-x-2 px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90 disabled:opacity-50"
            >
              <Save className="h-4 w-4" />
              <span>{t('common.save')}</span>
            </button>
          </form>
        </div>

        {/* Change Password */}
        <div className="bg-dark-800 rounded-lg p-6 border border-dark-700">
          <h2 className="text-xl font-semibold text-white mb-4">
            {t('profile.changePassword')}
          </h2>

          <form onSubmit={handlePasswordSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                {t('profile.currentPassword')}
              </label>
              <input
                type="password"
                value={passwordData.currentPassword}
                onChange={(e) => setPasswordData(prev => ({ ...prev, currentPassword: e.target.value }))}
                className="w-full px-3 py-2 bg-dark-700 border border-dark-500 rounded-md text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                {t('profile.newPassword')}
              </label>
              <input
                type="password"
                value={passwordData.newPassword}
                onChange={(e) => setPasswordData(prev => ({ ...prev, newPassword: e.target.value }))}
                className="w-full px-3 py-2 bg-dark-700 border border-dark-500 rounded-md text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                {t('auth.confirmPassword')}
              </label>
              <input
                type="password"
                value={passwordData.confirmPassword}
                onChange={(e) => setPasswordData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                className="w-full px-3 py-2 bg-dark-700 border border-dark-500 rounded-md text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="flex items-center space-x-2 px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90 disabled:opacity-50"
            >
              <Save className="h-4 w-4" />
              <span>{t('common.save')}</span>
            </button>
          </form>
        </div>
      </div>

      {/* Billing Information - Full Width */}
      <div className="bg-dark-800 rounded-lg p-6 border border-dark-700">
        <h2 className="text-xl font-semibold text-white mb-4 flex items-center">
          <Receipt className="h-5 w-5 text-primary mr-2" />
          Billing Information
        </h2>
        <p className="text-gray-400 text-sm mb-6">
          This information will be used for invoice generation. Fill in your details to receive proper tax invoices.
        </p>

        <form onSubmit={handleBillingSubmit} className="space-y-6">
          {/* Customer Type */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-3">Customer Type</label>
            <div className="flex space-x-4">
              <label className="flex items-center">
                <input
                  type="radio"
                  name="customer_type"
                  value="individual"
                  checked={billingInfo.customer_type === 'individual'}
                  onChange={(e) => setBillingInfo(prev => ({ ...prev, customer_type: e.target.value }))}
                  className="mr-2 text-primary focus:ring-primary"
                />
                <span className="text-white">Individual</span>
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  name="customer_type"
                  value="company"
                  checked={billingInfo.customer_type === 'company'}
                  onChange={(e) => setBillingInfo(prev => ({ ...prev, customer_type: e.target.value }))}
                  className="mr-2 text-primary focus:ring-primary"
                />
                <span className="text-white">Company</span>
              </label>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Company Name (only if company type) */}
            {billingInfo.customer_type === 'company' && (
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  <Building className="h-4 w-4 inline mr-1" />
                  Company Name *
                </label>
                <input
                  type="text"
                  value={billingInfo.company_name}
                  onChange={(e) => setBillingInfo(prev => ({ ...prev, company_name: e.target.value }))}
                  className="w-full px-3 py-2 bg-dark-700 border border-dark-500 rounded-md text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="Enter company name"
                />
              </div>
            )}

            {/* Street Address */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-300 mb-1">
                <MapPin className="h-4 w-4 inline mr-1" />
                Street Address *
              </label>
              <input
                type="text"
                value={billingInfo.street_address}
                onChange={(e) => setBillingInfo(prev => ({ ...prev, street_address: e.target.value }))}
                className="w-full px-3 py-2 bg-dark-700 border border-dark-500 rounded-md text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="Enter street address"
              />
            </div>

            {/* City */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                City *
              </label>
              <input
                type="text"
                value={billingInfo.city}
                onChange={(e) => setBillingInfo(prev => ({ ...prev, city: e.target.value }))}
                className="w-full px-3 py-2 bg-dark-700 border border-dark-500 rounded-md text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="Enter city"
              />
            </div>

            {/* Postal Code */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Postal Code *
              </label>
              <input
                type="text"
                value={billingInfo.postal_code}
                onChange={(e) => setBillingInfo(prev => ({ ...prev, postal_code: e.target.value }))}
                className="w-full px-3 py-2 bg-dark-700 border border-dark-500 rounded-md text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="Enter postal code"
              />
            </div>

            {/* Country */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Country *
              </label>
              <select
                value={billingInfo.country}
                onChange={(e) => setBillingInfo(prev => ({ ...prev, country: e.target.value }))}
                className="w-full px-3 py-2 bg-dark-700 border border-dark-500 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-primary"
              >
                {countries.map(country => (
                  <option key={country} value={country}>{country}</option>
                ))}
              </select>
            </div>

            {/* Phone */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Phone Number
              </label>
              <input
                type="tel"
                value={billingInfo.phone}
                onChange={(e) => setBillingInfo(prev => ({ ...prev, phone: e.target.value }))}
                className="w-full px-3 py-2 bg-dark-700 border border-dark-500 rounded-md text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="Enter phone number"
              />
            </div>

            {/* VAT Number (only if company type) */}
            {billingInfo.customer_type === 'company' && (
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  VAT Number / Tax ID
                </label>
                <input
                  type="text"
                  value={billingInfo.vat_number}
                  onChange={(e) => setBillingInfo(prev => ({ ...prev, vat_number: e.target.value }))}
                  className="w-full px-3 py-2 bg-dark-700 border border-dark-500 rounded-md text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="Enter VAT number or Tax ID (optional)"
                />
                <p className="text-xs text-gray-400 mt-1">
                  Enter your VAT number for proper tax handling (e.g., PL1234567890, GB123456789)
                </p>
              </div>
            )}
          </div>

          <div className="pt-4 border-t border-dark-600">
            <button
              type="submit"
              disabled={isLoading}
              className="flex items-center space-x-2 px-6 py-2 bg-primary text-white rounded-md hover:bg-primary/90 disabled:opacity-50"
            >
              <Save className="h-4 w-4" />
              <span>Save Billing Information</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default Profile